/**
 * Client for `POST /api/intents/reschedule` (tv-2na.11.8, W4-B): the keep-original
 * move as ONE server transaction — supersede the original, create the new booking
 * (same lifecycle status, back in the cadence) and re-point the deposit — instead
 * of the old independent write-through PUTs, where the repointed deposit could
 * land before its new booking and fail deposits_booking_fk (500).
 *
 * Same fetch/auth/clock shape and discriminated result as the wrappers in
 * `@/api/intents` (kept in its own module so the reschedule track doesn't edit
 * the shared intents client): never throws; a failure carries `kind`
 * ('slot_taken' | 'rejected' | 'transient'). The new id is client-minted and is
 * the idempotency key, so a retry after a transient failure is a safe no-op.
 */
import { authHeaders, isSigningIn, whenSignedIn } from '@/api/persist';
import { intentErrorKind, type IntentFailure } from '@/api/intents';
import { useSimulationStore } from '@/state/simulationStore';

export interface RescheduleIntentPayload {
  originalBookingId: string;
  newBookingId: string;
  artistId: string;
  newStartIso: string;
  reason: string;
  note?: string;
  by: string;
  /** The history entry's stamp. */
  at?: string;
  /** Override the sim clock (defaults to the facilitator's clock). */
  nowIso?: string;
}

/**
 * "No date" (tv-2na.11.13): `mode: 'release'` supersedes the booking with no new
 * slot and keeps its money on file for 12 months, in the same one transaction.
 */
export interface ReleaseIntentPayload {
  mode: 'release';
  originalBookingId: string;
  reason: string;
  note?: string;
  by: string;
  at?: string;
  nowIso?: string;
}

/** `alreadyExists`: an idempotent replay of a committed move / release (still a success). */
export type RescheduleIntentResult = { ok: true; alreadyExists: boolean } | IntentFailure;

export async function rescheduleIntent(
  payload: RescheduleIntentPayload | ReleaseIntentPayload,
): Promise<RescheduleIntentResult> {
  if (isSigningIn()) await whenSignedIn(); // B3: never on a previous persona's token mid-switch
  let res: Response;
  try {
    res = await fetch('/api/intents/reschedule', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ ...payload, nowIso: payload.nowIso ?? useSimulationStore.getState().clockIso }),
    });
  } catch {
    return { ok: false, kind: 'transient', status: 0, error: 'network' };
  }
  if (res.ok) {
    const body = (await res.json().catch(() => ({}))) as { alreadyExists?: unknown } | null;
    return { ok: true, alreadyExists: body?.alreadyExists === true };
  }
  const body = (await res.json().catch(() => undefined)) as
    | { error?: string; code?: string; message?: string }
    | undefined;
  return {
    ok: false,
    kind: intentErrorKind(res.status, body),
    status: res.status,
    error: body?.error ?? `http-${res.status}`,
    ...(typeof body?.code === 'string' ? { code: body.code } : {}),
    ...(body?.message ? { message: body.message } : {}),
  };
}
